"use client";

import { useMemo } from "react";
import Image from "next/image";
import { Menu, ShoppingCart, UserRound, X } from "lucide-react";
import { usePathname } from "next/navigation";
import Link from "next/link";

import { ThemeToggle } from "@/features/theme/components/ThemeToggle";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import type { UserRole } from "@/features/users/user.type";
import { Button } from "@/components/ui/button";
import { useCartTotalQuantity } from "@/features/shop/cart/cart.store";
import { getRoleHomePath, getRoleRoute } from "../role-paths";
import { Breadcrumb, type BreadcrumbItem } from "./breadcrumb";

interface AppHeaderProps {
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

const STATIC_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  cases: "Cases",
  farms: "Farms",
  farm: "Farm",
  shop: "Shop",
  shops: "My Shops",
  products: "Products",
  orders: "Orders",
  cart: "Cart",
  add: "Add",
  profile: "Profile",
  report: "Report Issue",
  officers: "Officer Management",
  farmers: "Farmers",
  checkout: "Checkout",
  history: "History",
  messages: "Messages",
};

const PARENT_DETAIL_LABELS: Record<string, string> = {
  cases: "Case Details",
  farms: "Farm Details",
  shop: "Shop Details",
  shops: "Shop Details",
  orders: "Order Details",
  products: "Product Details",
};

const HOME_LABELS: Partial<Record<UserRole, string>> = {
  admin: "Admin Center",
  farmer: "Overview",
  extension_officer: "Overview",
  veterinary_officer: "Overview",
  county_supervisor: "Overview",
  customer: "Shop",
};

function isDynamicSegment(segment: string) {
  return /^[0-9a-fA-F]{24}$/.test(segment) || /^\d+$/.test(segment);
}

function humanize(segment: string, parent?: string) {
  if (STATIC_LABELS[segment]) return STATIC_LABELS[segment];
  if (isDynamicSegment(segment)) {
    return (parent && PARENT_DETAIL_LABELS[parent]) || "Details";
  }

  return segment
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildBreadcrumbs(
  pathname: string,
  role?: UserRole | null,
): BreadcrumbItem[] {
  if (!role) return [];

  const segments = pathname.split("/").filter(Boolean);
  const home = getRoleHomePath(role);
  const items: BreadcrumbItem[] = [
    { label: HOME_LABELS[role] ?? "Home", url: home },
  ];

  if (!segments.length) return items;

  let currentPath = "";

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    if (index === 0) return;

    const parent = segments[index - 1];
    items.push({
      label: humanize(segment, parent),
      url: currentPath,
    });
  });

  return items;
}

export function AppHeader({ onMenuToggle, isSidebarOpen }: AppHeaderProps) {
  const pathname = usePathname();
  const { userAccount: user } = useAuth();
  const isShopRoute = pathname.startsWith("/shop");
  const profileHref = user?.role
    ? getRoleRoute(user.role, "profile")
    : "/login";
  const cartCount = useCartTotalQuantity();

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(pathname, user?.role),
    [pathname, user?.role],
  );

  return (
    <header className="z-40 h-16 shrink-0 border-b border-white/10 bg-card/80 backdrop-blur-md">
      <div className="flex h-full items-center justify-between gap-3 px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="md:hidden"
            aria-label={isSidebarOpen ? "Close navigation" : "Open navigation"}
          >
            {isSidebarOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative h-9 w-9 overflow-hidden rounded-lg border border-primary/10 bg-card shadow-xs">
              <Image
                src="/logo-0.png"
                alt="Efasol logo"
                fill
                sizes="36px"
                className="object-cover"
                priority
              />
            </div>
            <span className="font-bold text-base tracking-tight">Efasol</span>
          </div>

          <div className="min-w-0">
            <Breadcrumb items={breadcrumbs} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isShopRoute ? (
            <>
              {user ? (
                <>
                  <Button
                    asChild
                    variant="outline"
                    className="hidden md:inline-flex gap-2 bg-transparent"
                  >
                    <Link href={profileHref}>
                      <UserRound className="h-4 w-4" />
                      Account
                    </Link>
                  </Button>
                  <Button asChild className="relative hidden md:inline-flex gap-2">
                    <Link href="/shop/cart">
                      <ShoppingCart className="h-4 w-4" />
                      Cart
                      {cartCount > 0 ? (
                        <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-white">
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      ) : null}
                    </Link>
                  </Button>
                </>
              ) : (
                <Button
                  asChild
                  variant="outline"
                  className="hidden md:inline-flex bg-transparent"
                >
                  <Link href="/login">Login</Link>
                </Button>
              )}
            </>
          ) : null}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
